import { useEffect, useMemo, useState } from "react";

import api from "../api/client";
import { usePublishScreenData } from "../utils/aiScreenContext";
import { useCustomer } from "../context/CustomerContext";
import { useSort, sortRows, SortableTh } from "../components/SortableTh";
import { formatDateTime } from "../utils/datetime";

/**
 * Intune-managed device admin. Pick a customer, list their Graph
 * deviceManagement.managedDevices, and run sync / lock / reboot /
 * retire / wipe actions. Reuses each customer's M365TenantConfig
 * for auth — the customer's Azure AD app must have the
 * DeviceManagement* application permissions added.
 *
 * Destructive operations (wipe + retire + reboot + lock) call the
 * privileged Graph endpoints and require ``admin`` on the RMM side.
 * The buttons stay enabled for techs but the backend rejects them.
 */

type Device = {
  id: string;
  device_name: string | null;
  user_principal_name: string | null;
  operating_system: string | null;
  os_version: string | null;
  model: string | null;
  manufacturer: string | null;
  serial_number: string | null;
  compliance_state: string | null;
  enrolled_at: string | null;
  last_sync_at: string | null;
  management_agent: string | null;
  ownership: string | null;
  encrypted: boolean | null;
};

const COMPLIANCE_PILL: Record<string, string> = {
  compliant: "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300",
  noncompliant: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
  inGracePeriod: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
  configManager: "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200",
  conflict: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
  error: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
  unknown: "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200",
};

const COMPLIANCE_RANK: Record<string, number> = {
  compliant: 0,
  inGracePeriod: 1,
  configManager: 2,
  conflict: 3,
  noncompliant: 4,
  error: 5,
  unknown: 6,
};

export default function IntuneDevices() {
  const { currentCustomerId } = useCustomer();
  // Driven solely by the global customer switcher — no local picker.
  const customerId: number | "" = currentCustomerId ?? "";
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState<{ id: string; action: string } | null>(null);
  const [results, setResults] = useState<Record<string, string>>({});
  const sort = useSort("device_name");

  // Publish the visible rows so the global AI Assist can answer
  // "what's on my screen". Projected explicitly - credential and
  // machine-output fields must not reach a prompt.
  usePublishScreenData(
    "Managed mobile devices",
    useMemo(
      () =>
        devices.map((d) => ({
          name: d.device_name,
          user: d.user_principal_name,
          os: d.operating_system,
          os_version: d.os_version,
          model: d.model,
          compliance: d.compliance_state,
          last_sync_at: d.last_sync_at,
        })),
      [devices],
    ),
    undefined,
  );

  const load = async () => {
    if (customerId === "") return;
    setErr("");
    setLoading(true);
    try {
      const r = await api.get(`/api/v1/intune/${customerId}/devices`);
      setDevices(r.data || []);
    } catch (e: unknown) {
      const ex = e as { response?: { data?: { detail?: string } } };
      setErr(ex.response?.data?.detail || "Failed to load Intune devices");
      setDevices([]);
    } finally {
      setLoading(false);
    }
  };

  // Auto-load whenever the selected customer changes.
  useEffect(() => {
    if (customerId !== "") load();
    else setDevices([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const runAction = async (
    device: Device,
    action: "sync" | "lock" | "reboot" | "retire" | "wipe",
    confirmText?: string,
  ) => {
    if (confirmText && !window.confirm(confirmText)) return;
    if (customerId === "") return;
    setBusy({ id: device.id, action });
    setResults((r) => ({ ...r, [`${device.id}:${action}`]: "" }));
    try {
      const r = await api.post(`/api/v1/intune/${customerId}/devices/${device.id}/${action}`);
      setResults((prev) => ({
        ...prev,
        [`${device.id}:${action}`]: `OK — ${r.data?.message || "accepted"}`,
      }));
    } catch (e: unknown) {
      const ex = e as { response?: { data?: { detail?: string } } };
      setResults((prev) => ({
        ...prev,
        [`${device.id}:${action}`]: `Failed: ${ex.response?.data?.detail || "error"}`,
      }));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Intune devices</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Per-customer Intune-managed devices via Microsoft Graph. Reuses the customer's M365 tenant
          config; the Azure AD app needs{" "}
          <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">
            DeviceManagementManagedDevices.Read.All
          </code>{" "}
          (and{" "}
          <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">
            .PrivilegedOperations.All
          </code>{" "}
          for wipe / retire / reboot / lock).
        </p>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-4 flex items-center gap-3 flex-wrap">
        <span className="text-sm text-gray-600 dark:text-gray-300 flex-1 min-w-[220px]">
          {customerId === ""
            ? "Select a customer in the header switcher to view their Intune devices."
            : "Showing the selected customer's Intune-managed devices."}
        </span>
        <button
          onClick={load}
          disabled={customerId === "" || loading}
          className="px-4 py-2 bg-brand text-white rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50"
        >
          {loading ? "Loading…" : "Load devices"}
        </button>
      </div>

      {err && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300 px-4 py-2 rounded-lg text-sm">
          {err}
        </div>
      )}

      {devices.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-900 text-gray-700 dark:text-gray-300">
                <SortableTh label="Device" sortKey="device_name" sort={sort.sort} onSort={sort.toggle} />
                <SortableTh label="User" sortKey="user_principal_name" sort={sort.sort} onSort={sort.toggle} />
                <SortableTh label="OS" sortKey="operating_system" sort={sort.sort} onSort={sort.toggle} />
                <SortableTh label="Compliance" sortKey="compliance_state" sort={sort.sort} onSort={sort.toggle} />
                <SortableTh label="Last sync" sortKey="last_sync_at" sort={sort.sort} onSort={sort.toggle} />
                <th className="text-left px-3 py-2 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {sortRows(devices, sort.sort, (d, key) => {
                switch (key) {
                  case "user_principal_name":
                    return (d.user_principal_name || "").toLowerCase();
                  case "operating_system":
                    return (d.operating_system || "").toLowerCase();
                  case "compliance_state":
                    return COMPLIANCE_RANK[d.compliance_state || "unknown"] ?? COMPLIANCE_RANK.unknown;
                  case "last_sync_at":
                    return d.last_sync_at ? Date.parse(d.last_sync_at) : 0;
                  default:
                    return (d.device_name || d.id || "").toLowerCase();
                }
              }).map((d) => {
                const pill =
                  COMPLIANCE_PILL[d.compliance_state || "unknown"] || COMPLIANCE_PILL.unknown;
                return (
                  <tr key={d.id} className="text-gray-900 dark:text-gray-100">
                    <td className="px-3 py-2">
                      <div className="font-medium">{d.device_name || d.id}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {d.manufacturer} {d.model} · {d.ownership}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-xs">{d.user_principal_name || "—"}</td>
                    <td className="px-3 py-2 text-xs">
                      {d.operating_system} {d.os_version}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${pill}`}>
                        {d.compliance_state || "unknown"}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">
                      {d.last_sync_at ? formatDateTime(d.last_sync_at) : "—"}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <ActionBtn
                          label="Sync"
                          variant="default"
                          onClick={() => runAction(d, "sync")}
                          busy={busy?.id === d.id && busy.action === "sync"}
                          result={results[`${d.id}:sync`]}
                        />
                        <ActionBtn
                          label="Lock"
                          variant="warn"
                          onClick={() => runAction(d, "lock")}
                          busy={busy?.id === d.id && busy.action === "lock"}
                          result={results[`${d.id}:lock`]}
                        />
                        <ActionBtn
                          label="Reboot"
                          variant="warn"
                          onClick={() =>
                            runAction(
                              d,
                              "reboot",
                              `Reboot ${d.device_name}? The user will lose any unsaved work.`,
                            )
                          }
                          busy={busy?.id === d.id && busy.action === "reboot"}
                          result={results[`${d.id}:reboot`]}
                        />
                        <ActionBtn
                          label="Retire"
                          variant="danger"
                          onClick={() =>
                            runAction(
                              d,
                              "retire",
                              `Retire ${d.device_name} from Intune? Corporate apps will be removed; personal data is kept.`,
                            )
                          }
                          busy={busy?.id === d.id && busy.action === "retire"}
                          result={results[`${d.id}:retire`]}
                        />
                        <ActionBtn
                          label="Wipe"
                          variant="danger"
                          onClick={() =>
                            runAction(
                              d,
                              "wipe",
                              `FACTORY RESET ${d.device_name}? All data on the device will be erased. This cannot be undone.`,
                            )
                          }
                          busy={busy?.id === d.id && busy.action === "wipe"}
                          result={results[`${d.id}:wipe`]}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {devices.length === 0 && customerId !== "" && !loading && !err && (
        <div className="text-sm text-gray-500 dark:text-gray-400 italic">
          No Intune-managed devices found for this customer.
        </div>
      )}
    </div>
  );
}

function ActionBtn({
  label,
  variant,
  onClick,
  busy,
  result,
}: {
  label: string;
  variant: "default" | "warn" | "danger";
  onClick: () => void;
  busy: boolean;
  result?: string;
}) {
  const variantClass =
    variant === "danger"
      ? "text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30"
      : variant === "warn"
        ? "text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/30"
        : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/40";
  return (
    <div className="flex flex-col">
      <button
        onClick={onClick}
        disabled={busy}
        className={`text-xs font-medium px-2 py-1 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-50 ${variantClass}`}
        title={result || undefined}
      >
        {busy ? "…" : label}
      </button>
      {result && (
        <span
          className={`text-[10px] mt-0.5 max-w-[120px] truncate ${
            result.startsWith("OK") ? "text-green-700 dark:text-green-400" : "text-red-700 dark:text-red-400"
          }`}
        >
          {result}
        </span>
      )}
    </div>
  );
}
